import { useEffect, useMemo, useRef, useState } from 'react';
import type { Action, GameState } from '../../engine';
import type { Emote } from '../../worker/protocol';
import { LocalGame, clearSolo, loadSolo, soloSeats } from '../game/localGame';
import type { TableHandlers, TableModel } from '../game/types';
import { navigate, speedFromUrl } from '../lib/router';
import { TableScreen } from './TableScreen';
import type { IncomingEmote } from './TableScreen';

/** Vs-computer: binds a LocalGame (saved after every move) to the table. */
export function SoloScreen() {
  const [game] = useState(() => {
    const save = loadSolo();
    return save ? new LocalGame(save, { speed: speedFromUrl() }) : null;
  });
  const [view, setView] = useState<GameState | null>(() => game?.view ?? null);
  const [emotes, setEmotes] = useState<IncomingEmote[]>([]);
  const emoteSeq = useRef(1000);

  useEffect(() => {
    if (!game) {
      navigate({ name: 'home' }, true);
      return;
    }
    game.onChange = setView;
    game.start();
    return () => game.stop();
  }, [game]);

  const handlers = useMemo<TableHandlers>(
    () => ({
      act: (a: Action) => {
        game?.act(a);
      },
      ready: () => {
        game?.act({ type: 'nextHole' });
      },
      emote: () => {
        // A computer player may answer with a sticker of its own.
        if (!game || Math.random() < 0.4) return;
        const bots = game.save.state.players.map((p, i) => ({ p, i })).filter((x) => x.p.isBot);
        const pick = bots[Math.floor(Math.random() * bots.length)];
        if (!pick) return;
        const replies: Emote[] = ['well-played', 'smug', 'fore'];
        const emote = replies[Math.floor(Math.random() * replies.length)]!;
        setTimeout(() => setEmotes((l) => [...l.slice(-5), { id: ++emoteSeq.current, seat: pick.i, emote }]), 900);
      },
      playAgain: () => game?.rematch(),
      home: () => navigate({ name: 'home' }),
      quit: () => {
        game?.stop();
        clearSolo();
        navigate({ name: 'home' });
      },
      pause: (p: boolean) => game?.setPaused(p),
    }),
    [game],
  );

  if (!game || !view) return null;
  const seats = soloSeats(game.save).map((s, i) => (i === 0 ? { ...s, name: game.save.humanName || 'You' } : s));
  const model: TableModel = { mode: 'solo', state: view, me: 0, isHost: true, seats, gameId: `solo:${game.save.state.seed}` };
  return <TableScreen model={model} handlers={handlers} incomingEmotes={emotes} />;
}
